import React, { FC, useEffect, useState } from 'react'
import { Button, Checkbox, Modal } from '@nalezhytyi/saldo-ui'
import { observer } from 'mobx-react'
import invoicesStore from 'store/mobx/InvoicesStore'
import { useAmplitude } from 'react-amplitude-hooks'
import starIcon from 'assets/images/star.png'
import priceCheck from 'assets/images/price-check-icon.svg'
import peopleIcon from 'assets/images/people-icon.svg'
import documentIcon from 'assets/images/document-icon.svg'
import lightningIcon from 'assets/images/lightning-icon.svg'
import shieldIcon from 'assets/images/shield-icon.svg'
import starsIcon from 'assets/images/stars-icon.svg'
import stocksIcon from 'assets/images/stocks-icon.svg'
import lock from 'assets/images/lock-icon.svg'
import Lottie from 'lottie-react'
import successAnimation from './check.json'
import { CardForm } from './CardForm'
import settingsStore from 'store/mobx/SettingsStore'
import authStore from 'store/mobx/AuthStore'
import { useHistory } from 'react-router'
import useNotification from 'scripts/hooks/useNotification'
import useLegalFlow from 'hooks/useLegalFlow'

import css from './styles.module.scss'

export const Paywall: FC = observer(() => {
  const { isPayWall, isPayWallSource } = invoicesStore
  const { isHardPaywall, currentStripePlan } = settingsStore
  const notification = useNotification()
  const isLegalFlow = useLegalFlow()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isSuccessSubscribe, setIsSuccessSubscribe] = useState<boolean>(false)
  const [isPrePaywallOpen, setIsPrePaywallOpen] = useState<boolean>(false)
  const [showMainAfterPre, setShowMainAfterPre] = useState<boolean>(false)
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false)
  const history = useHistory()

  const { logEvent } = useAmplitude()

  useEffect(() => {
    if (isPayWall) {
      if (isPayWallSource === 'Create Button') {
        setIsPrePaywallOpen(true)
        setShowMainAfterPre(false) // ensure gated until user proceeds
      }
      logEvent('subscription_paywallShown', {
        Source: isPayWallSource || 'Undefined',
      })
      if (isPayWallSource === 'Create Button') {
        history.push('/all-documents/INV')
      }
    } else {
      setIsPrePaywallOpen(false)
      setShowMainAfterPre(false)
    }
  }, [isPayWall, isPayWallSource])

  return (
    <>
      {/* Pre-Paywall Modal: shown before main paywall when source is "Create Button" */}
      <Modal
        size="sm"
        onClose={() => {
          setIsPrePaywallOpen(false)
          setShowMainAfterPre(false)
          invoicesStore.setIsPaywall(false)
        }}
        open={isPrePaywallOpen}
        dialogClassName="paywallModal"
        backdrop={true}
      >
        <Modal.Header>
          <Modal.Title bold>
            <h1 className={css.prePaywallTitle}>
              You&apos;ve reached your limit
            </h1>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className={css.prePaywallIcon}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z"></path>
              <path d="M5 21h14"></path>
            </svg>
          </div>
          <div className={css.promoIntro}>
            <div className={css.promoTitle}>Ready to grow your business?</div>
            <div className={css.promoSubtitle}>
              You&apos;ve used your free invoice! Upgrade to Pro and create
              unlimited invoices, manage more clients, and unlock powerful
              features.
            </div>
          </div>

          <div className={css.featureItems}>
            {[
              'Unlimited Invoices',
              'Unlimited Clients',
              'Online Payments',
              'Custom Branding',
            ].map((title) => (
              <div
                key={title}
                className={`${css.featureItem} ${css.featureItemNoBg}`}
              >
                <span
                  className={`${css.featureItemIcon} ${css.featureItemIconNoBg}`}
                >
                  <img src={priceCheck} alt="icon" />
                </span>
                <div className={css.featureItemContent}>
                  <div>{title}</div>
                </div>
              </div>
            ))}
          </div>

          <Button
            appearance="primary"
            size="md"
            block
            onClick={() => {
              setShowMainAfterPre(true)
              setIsPrePaywallOpen(false)
            }}
            className={css.prePaywallButton}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"></path>
            </svg>
            Upgrade to Pro
          </Button>
          <Button
            appearance="ghost"
            size="md"
            block
            onClick={() => {
              // Close both pre-paywall and paywall
              setIsPrePaywallOpen(false)
              setShowMainAfterPre(false)
              invoicesStore.setIsPaywall(false)
            }}
          >
            Maybe Later
          </Button>
        </Modal.Body>
      </Modal>

      {/* Main Paywall Modal */}
      <Modal
        size="sm"
        onClose={() => {
          setShowMainAfterPre(false)
          invoicesStore.setIsPaywall(false)
        }}
        open={
          isPayWall &&
          !isPrePaywallOpen &&
          (isPayWallSource === 'Create Button' ? showMainAfterPre : true)
        }
        dialogClassName="paywallModal"
        backdrop={isHardPaywall ? 'static' : true}
      >
        <Modal.Header hideCloseIcon={isHardPaywall}>
          <Modal.Title bold>
            <h1 className={css.paywallTitle}>
              <div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z"></path>
                  <path d="M5 21h14"></path>
                </svg>
              </div>
              Invoice Maker PRO
            </h1>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {!isLegalFlow && (
            <div className={css.feedback}>
              <div className={css.starsRow}>
                {[...Array(5)].map((_, i) => (
                  <img key={i} src={starIcon} alt="star" />
                ))}
                <span className={css.ratingText}>4.9/5 from 2,847+ users</span>
              </div>
              <div className={css.feedbackQuote}>
                &quot;Invoice Maker Pro transformed how I manage my business
                finances!&quot;
              </div>
            </div>
          )}

          <div className={css.promoIntro}>
            <div className={css.promoTitle}>
              Scale Your Business Without Limits
            </div>
            <div className={css.promoSubtitle}>
              Join thousands of entrepreneurs who&#39;ve upgraded to streamline
              their invoicing and get paid faster
            </div>
          </div>
          <div className={css.promoBlock}>
            {!isLegalFlow && (
              <div className={css.promoBadge}>🔥 Limited Time: 50% OFF</div>
            )}
            <div className={css.promoPriceRow}>
              {!isLegalFlow && (
                <span className={css.promoOldPrice}>$39.99</span>
              )}
              <span className={css.promoNewPrice}>
                ${currentStripePlan.price}
              </span>
              <span className={css.promoPer}>/month</span>
            </div>
            {!isLegalFlow && (
              <div className={css.promoExpire}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
                Offer expires in 24 hours
              </div>
            )}
          </div>
          <div className={css.featureItems}>
            {[
              {
                icon: documentIcon,
                title: 'Unlimited Invoices',
                subtitle: 'Create as many invoices as you need',
              },
              {
                icon: peopleIcon,
                title: 'Unlimited Clients',
                subtitle: 'Manage all your clients in one place',
              },
              {
                icon: lightningIcon,
                title: 'Online Payments',
                subtitle: 'Get paid faster with integrated payments',
              },
              {
                icon: starsIcon,
                title: 'Custom Branding',
                subtitle: 'Add your logo and brand colors',
              },
              {
                icon: shieldIcon,
                title: 'Cloud Backup',
                subtitle: 'Your data is always safe and secure',
              },
              {
                icon: stocksIcon,
                title: 'Advanced Analytics',
                subtitle: 'Track your business performance',
              },
            ].map(({ icon, title, subtitle }) => (
              <div key={title} className={css.featureItem}>
                <span className={css.featureItemIcon}>
                  <img src={icon} alt="icon" />
                </span>
                <div className={css.featureItemContent}>
                  <div className={css.featureItemTitle}>{title}</div>
                  <div className={css.featureItemSubtitle}>{subtitle}</div>
                </div>
              </div>
            ))}
          </div>

          <CardForm
            disabled={isLegalFlow && !termsAccepted}
            isLegalFlow={isLegalFlow}
            setIsLoading={setIsLoading}
            actionEnabled={!isLoading}
            onSave={async (token) => {
              logEvent('subscription_subscribe_clicked', {
                Source: 'Paywall',
              })

              if (!token) {
                notification.push('Invalid credentials.', {
                  type: 'error',
                })
                setIsLoading(false)

                return
              }

              notification.push('Subscription is connecting, wait a while.', {
                type: 'info',
              })

              await settingsStore.subscribeStripe(token, async () => {
                logEvent('subscription_subscribedSuccessfully', {
                  Source: 'Paywall',
                })

                if (typeof window.fbq === 'function') {
                  window.fbq('track', 'Purchase', {
                    value: currentStripePlan.price,
                    isHardPaywall,
                    Source: 'Paywall',
                  })
                }

                window.dataLayer = window.dataLayer || []
                window.dataLayer.push({
                  event: 'purchase',
                  currency: 'USD',
                  value: currentStripePlan.price,
                })

                notification.push('Subscription is successfully connected.', {
                  type: 'info',
                })
                await authStore.getUser()
                invoicesStore.setIsPaywall(false)
                setIsSuccessSubscribe(true)
              })
              setIsLoading(false)
            }}
          />
          {isLegalFlow && (
            <div className={css.termsBlock}>
              <Checkbox
                checked={termsAccepted}
                onChange={(_value: any, checked: boolean) =>
                  setTermsAccepted(checked)
                }
              />
              <span className={css.termsText}>
                I acknowledge that I have read and agree to the{' '}
                <a
                  href="https://telegra.ph/Privacy-Policy-11-08-8"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a
                  href="https://telegra.ph/Terms-of-Use-11-08"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms of Use
                </a>
                . I understand that my subscription will be charged $
                {currentStripePlan.price} USD every month and will automatically
                renew until cancelled. I can cancel my subscription at any time
                and retain all my data.
              </span>
            </div>
          )}
          <div className={css.footerGuarantee}>
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"></path>
              </svg>
              100% Risk-Free Guarantee
            </div>
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5"></path>
              </svg>
              14-day money-back guarantee
            </div>
            <div>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5"></path>
              </svg>{' '}
              Cancel anytime • Keep all your data
            </div>
          </div>
          <div className={css.paywallFooter}>
            <div className="mb-8 pt16">
              <img src={lock} alt="lock" />
              256-bit SSL encryption • PCI DSS compliant
            </div>
            {!isLegalFlow && (
              <div>
                By subscribing, you agree to our{' '}
                <a
                  href="https://telegra.ph/Terms-of-Use-11-08"
                  target="_blank"
                  rel="noreferrer"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="https://telegra.ph/Privacy-Policy-11-08-8"
                  target="_blank"
                  rel="noreferrer"
                >
                  Privacy Policy
                </a>
                . Your subscription will auto-renew monthly. Cancel anytime.
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        size="sm"
        onClose={() => setIsSuccessSubscribe(false)}
        open={isSuccessSubscribe}
        dialogClassName="paywallModal"
      >
        <Modal.Body style={{ padding: 0 }}>
          <Lottie
            animationData={successAnimation}
            className={css.successAnimation}
            loop={false}
          />
          <h1 className="text-center">Thank you!</h1>
          <div className={css.successModalDescription}>
            You’ve successfully upgraded
            <br /> to Invoice Maker PRO
          </div>
          <Button
            appearance="primary"
            size="md"
            block
            className={css.paywallButton}
            onClick={() => setIsSuccessSubscribe(false)}
          >
            Done
          </Button>
        </Modal.Body>
      </Modal>
    </>
  )
})
